import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { MessageService } from '../../../core/service/message.service';
import { BuildingSpaceService } from '../../service/data/building-space.service';
import { BuildingSpace } from '../../model/building-space';
import { Building } from '../../model/building';
import { Subscription, switchMap } from 'rxjs';
import { ObjectComponentCategory } from '../../model/object-component-category';
import { ObjectComponentCategoryService } from '../../service/data/object-component-category.service';
import { ObjectComponent } from '../../model/object-component';
import { FakeGuidService } from '../../../core/service/fake-guid.service';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { CustomProperty } from '../../model/custom-property';
import { ObjectComponentPropertyValue } from '../../model/object-component-property-value';
import { BuildingSpaceTypeService } from '../../service/data/building-space-type.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { PlannedMaintenanceAction } from '../../model/object-component-planned-maintenance';

@Component({
    selector: 'app-edit-building-space-dialog',
    templateUrl: './edit-building-space-dialog.component.html',
    styleUrls: ['./edit-building-space-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditBuildingSpaceDialogComponent implements OnInit {
  constructor(
    private _dialogService: ActionWindowService,
    private _dataService: BuildingSpaceService,
    private _buildingSpaceTypeService: BuildingSpaceTypeService,
    private _messageService: MessageService,
    private _componentCategoryService: ObjectComponentCategoryService,
    private _customPropertyService: CustomPropertyService,
    private _fb: FormBuilder,
    private _fcf: FormControlExFactory
  ) {}

  @Input() dataContext: { id: string };

  form: FormGroup;
  componentCategories: ObjectComponentCategory[];
  buildingSpace: BuildingSpace;

  cancel() {
    this._dialogService.close();
  }

  customProperties: CustomProperty[];

  private loadSub: Subscription;
  ngOnInit() {
    this.loadSub = this._customPropertyService
      .getList()
      .pipe(
        switchMap(val => {
          this.customProperties = val;
          return this._componentCategoryService.getList();
        }),
        switchMap(list => {
          this.componentCategories = list;
          for (let category of this.componentCategories) {
            for (let type of category.objectComponentTypes) {
              for (let property of type.customProperties) {
                property.property = this.customProperties.find(p => p.id === (property.property as unknown));
              }
            }
          }
          return this._dataService.get(this.dataContext.id);
        })
      )
      .subscribe(space => {
        this.form = this._fb.group({
          id: this._fcf.createHidden(space.id),
          buildingSpaceTypeId: this._fcf.createMandatory('BUILDING_SPACE_TYPE.BUILDING_SPACE_TYPE', space.buildingSpaceTypeId),
          caption: this._fcf.createWithOptions('BUILDING_SPACE.CAPTION', space.caption, { required: true, length: 50 }),
          area: this._fcf.create('BUILDING_SPACE.AREA', space.area),
          spaceNumber: this._fcf.createMaxLength('BUILDING_SPACE.SPACE_NUMBER', space.spaceNumber, 20),
          components: this._fb.array([]),
        });
        this.buildingSpace = space;
        this.components = space.components;
        for (let component of this.components) {
          for (let property of component.customPropertyValues) {
            property.customProperty = this.customProperties.find(p => p.id === property.customProperty);
          }
        }
      });
    this.loadSub.add(
      this._buildingSpaceTypeService.getList().subscribe(res => {
        this.buildingSpaceTypes = res.map(t => new SelectionItem(t.toString(), t.id));
      })
    );
  }

  components: ObjectComponent[];
  buildingSpaceTypes: SelectionItem[] = [];

  addComponent() {
    this.components.push(
      new ObjectComponent({
        componentCategory: this.componentCategories[0].id,
        componentType: this.componentCategories[0].objectComponentTypes[0].id,
      })
    );
    this.form.markAsDirty();
  }

  private _working = false;
  onSubmit() {
    if (this._working) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this._working = true;

    let model = new BuildingSpace(this.form.value);
    model.components = (this.form.controls['components'] as FormArray).controls.map(c => {
      let comp = new ObjectComponent(c.value);
      comp.customPropertyValues = ((c as FormGroup).controls['customPropertyValues'] as FormArray).controls.map(pf => {
        let value = pf.value;
        value.customProperty = this.customProperties.find(p => p.id === value.customProperty);
        return new ObjectComponentPropertyValue(value);
      });
      // At this component we expect to get 1 or 0 actions
      comp.plannedMaintenanceActions = ((c as FormGroup).controls['plannedMaintenanceActions'] as FormArray).controls
        .map(c => c as FormGroup)
        .map(a => new PlannedMaintenanceAction(a.value))
        .filter(
          a =>
            a.plannedMaintenanceCaption &&
            a.plannedMaintenanceCaption.trim().length > 0 &&
            a.plannedMaintenanceInterval &&
            a.plannedMaintenanceInterval > 0
        )
        .slice(0, 1);
      return comp;
    });
    this._dataService.change(model).subscribe(
      res => {
        this._messageService.showSuccess();
        this._dialogService.complete(res);
      },
      () => {
        this._working = false;
      }
    );
  }
}
